/**
 * Ad Service - Issue #204: Google Ads Implementation
 * Issue #311: Added interstitial and native ad support
 * Unified ad service for AdSense (web) and AdMob (mobile via Capacitor)
 * Follows pushNotificationService pattern
 */

import { isNativeMobile, isWeb } from '../utils/platform';
import { Capacitor, registerPlugin } from '@capacitor/core';

// Google test ad unit IDs - these show official "Test Ad" banners
// Replace with real IDs after AdSense/AdMob account approval
const ADSENSE_CLIENT_ID = 'ca-pub-3940256099942544';
const ADMOB_BANNER_ID = 'ca-app-pub-3940256099942544/6300978111';
const ADMOB_REWARDED_ID = 'ca-app-pub-3940256099942544/5224354917';
// Issue #311: Interstitial ad IDs
const ADMOB_INTERSTITIAL_ID = 'ca-app-pub-3940256099942544/1033173712';

let adMobPlugin = null;
let initialized = false;

/**
 * Dynamically load AdSense script into the page (only after GDPR consent)
 */
const loadAdSenseScript = () => {
  return new Promise((resolve, reject) => {
    if (document.querySelector('script[src*="adsbygoogle"]')) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${ADSENSE_CLIENT_ID}`;
    script.async = true;
    script.crossOrigin = 'anonymous';
    script.onload = resolve;
    script.onerror = () => reject(new Error('Failed to load AdSense script'));
    document.head.appendChild(script);
  });
};

/**
 * Initialize AdMob plugin on native mobile
 * Uses Capacitor plugin registry instead of npm import to avoid webpack bundling issues
 */
const initializeAdMob = async (npa = true) => {
  try {
    // Check if the AdMob plugin is registered with Capacitor
    // This avoids webpack trying to resolve @capacitor-community/admob at build time
    if (!Capacitor.isPluginAvailable('AdMob')) {
      console.warn('[ADS] AdMob plugin not installed');
      return;
    }

    adMobPlugin = registerPlugin('AdMob');
    await adMobPlugin.initialize({
      initializeForTesting: true, // Set to false in production
      requestNonPersonalizedAdsOnly: npa,
    });
  } catch (error) {
    console.warn('[ADS] AdMob plugin not available:', error);
  }
};

/**
 * Initialize the ad service based on platform
 * @param {boolean} npa - Non-Personalized Ads mode (default true until explicit consent)
 */
const initialize = async (npa = true) => {
  if (initialized) return;

  try {
    if (isWeb()) {
      // Set NPA flag before loading AdSense script
      window.adsbygoogle = window.adsbygoogle || [];
      window.adsbygoogle.requestNonPersonalizedAds = npa ? 1 : 0;
      await loadAdSenseScript();
    } else if (isNativeMobile()) {
      await initializeAdMob(npa);
    }
    initialized = true;
    console.log('[ADS] Ad service initialized for platform:', isWeb() ? 'web' : 'mobile', '| NPA:', npa);
  } catch (error) {
    console.warn('[ADS] Ad service initialization failed:', error);
    // Don't throw - graceful failure per AC-7, AC-11
  }
};

/**
 * Show a banner ad
 * On web: triggers AdSense ad refresh for the given element
 * On mobile: shows AdMob banner
 */
const showBanner = async (options = {}) => {
  if (!initialized) return;

  try {
    if (isNativeMobile() && adMobPlugin) {
      await adMobPlugin.showBanner({
        adId: ADMOB_BANNER_ID,
        adSize: 'BANNER',
        position: 'BOTTOM_CENTER',
        ...options,
      });
    } else if (isWeb()) {
      // AdSense banners are rendered via the <ins> element in AdBanner component
      // Push to adsbygoogle queue to render
      try {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
      } catch (e) {
        // Silent failure - ad may already be loaded or blocked
      }
    }
  } catch (error) {
    // Silent failure - AC-7, AC-11
    console.warn('[ADS] Failed to show banner:', error);
  }
};

/**
 * Show a rewarded video ad (mobile only)
 * Returns true if reward was earned, false otherwise
 */
const showRewarded = async () => {
  if (!initialized || !isNativeMobile() || !adMobPlugin) return false;

  try {
    await adMobPlugin.prepareRewardVideoAd({
      adId: ADMOB_REWARDED_ID,
    });

    const result = await adMobPlugin.showRewardVideoAd();
    return result?.type === 'earned_reward';
  } catch (error) {
    console.warn('[ADS] Failed to show rewarded ad:', error);
    return false;
  }
};

/**
 * Hide banner ad (mobile only)
 */
const hideBanner = async () => {
  if (!isNativeMobile() || !adMobPlugin) return;

  try {
    await adMobPlugin.hideBanner();
  } catch (error) {
    console.warn('[ADS] Failed to hide banner:', error);
  }
};

/**
 * Issue #311: Show an interstitial (full-screen) ad
 * Returns true if ad was shown successfully, false otherwise
 */
const showInterstitial = async () => {
  if (!initialized) return false;

  try {
    if (isNativeMobile() && adMobPlugin) {
      // Prepare and show interstitial on mobile
      await adMobPlugin.prepareInterstitial({
        adId: ADMOB_INTERSTITIAL_ID,
      });
      await adMobPlugin.showInterstitial();
      return true;
    } else if (isWeb()) {
      // Web interstitials are handled by the InterstitialAd component (demo mode)
      // Real AdSense interstitials require different integration
      return false;
    }
  } catch (error) {
    console.warn('[ADS] Failed to show interstitial:', error);
    return false;
  }

  return false;
};

/**
 * Issue #311: Show a native ad (in-feed)
 * On web: triggers AdSense native ad refresh
 * On mobile: handled by AdMob native ad integration
 */
const showNativeAd = async () => {
  if (!initialized) return;

  try {
    if (isWeb()) {
      // Push to adsbygoogle queue for native ad rendering
      try {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
      } catch (e) {
        // Silent failure - ad may already be loaded or blocked
      }
    }
    // Native ads on mobile are typically rendered differently
    // and may require AdMob Native Advanced integration
  } catch (error) {
    console.warn('[ADS] Failed to show native ad:', error);
  }
};

export default {
  initialize,
  showBanner,
  showRewarded,
  hideBanner,
  showInterstitial,
  showNativeAd,
};
